import { useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";

interface DraftMessage {
  [channelUrl: string]: string;
}

/**
 * 채팅 메시지 초안을 관리하는 커스텀 훅
 * localStorage를 사용하여 초안을 저장하고 복원합니다.
 */
export const useChatDraft = (
  currentChannel: any,
  showChannel: boolean,
  isUpdatingChannel: boolean,
) => {
  const draftObserverRef = useRef<MutationObserver | null>(null);

  // localStorage에서 초안 삭제
  const deleteDraftFromLocalStorage = useCallback((channelUrl: string) => {
    if (!channelUrl) return;

    try {
      const savedDrafts = localStorage.getItem("sendbird_draft_messages");
      const drafts: DraftMessage = savedDrafts ? JSON.parse(savedDrafts) : {};

      if (drafts[channelUrl]) {
        delete drafts[channelUrl];
        localStorage.setItem("sendbird_draft_messages", JSON.stringify(drafts));
      }
    } catch (error) {
      console.error("Failed to delete draft:", error);
    }
  }, []);

  // localStorage에 초안 저장
  const saveDraftToLocalStorage = useCallback(
    (channelUrl: string, draft: string) => {
      if (!channelUrl) return;

      try {
        const savedDrafts = localStorage.getItem("sendbird_draft_messages");
        const drafts: DraftMessage = savedDrafts ? JSON.parse(savedDrafts) : {};

        if (draft && draft.trim()) {
          drafts[channelUrl] = draft;
          localStorage.setItem(
            "sendbird_draft_messages",
            JSON.stringify(drafts),
          );
        } else {
          deleteDraftFromLocalStorage(channelUrl);
        }
      } catch (error) {
        console.error("Failed to save draft:", error);
      }
    },
    [deleteDraftFromLocalStorage],
  );

  // localStorage에서 초안 복원
  const restoreDraftFromLocalStorage = useCallback((channelUrl: string) => {
    if (!channelUrl) return;

    try {
      const savedDrafts = localStorage.getItem("sendbird_draft_messages");
      const drafts: DraftMessage = savedDrafts ? JSON.parse(savedDrafts) : {};
      const draft = drafts[channelUrl];

      if (draft) {
        // DOM이 준비될 때까지 대기
        const tryRestore = (attempts = 0) => {
          if (attempts > 10) return;

          let messageInput = document.querySelector(
            ".sendbird-message-input-text-field",
          ) as HTMLTextAreaElement;

          if (!messageInput) {
            messageInput = document.querySelector(
              'textarea[name="sendbird-message-input-text-field"]',
            ) as HTMLTextAreaElement;
          }

          if (!messageInput) {
            messageInput = document.querySelector(
              ".sendbird-message-input__input",
            ) as HTMLTextAreaElement;
          }

          if (messageInput) {
            messageInput.value = draft;
            messageInput.focus();
            messageInput.setSelectionRange(draft.length, draft.length);

            // React 이벤트 트리거
            const event = new Event("input", { bubbles: true });
            messageInput.dispatchEvent(event);

            const changeEvent = new Event("change", { bubbles: true });
            messageInput.dispatchEvent(changeEvent);

            // 높이 조정
            messageInput.style.height = "auto";
            messageInput.style.height = `${messageInput.scrollHeight}px`;
          } else {
            setTimeout(() => tryRestore(attempts + 1), 100);
          }
        };

        tryRestore();
      }
    } catch (error) {
      console.error("Failed to restore draft:", error);
    }
  }, []);

  // Debounced 저장 함수
  const debouncedSaveDraft = useRef(
    debounce((channelUrl: string, text: string) => {
      saveDraftToLocalStorage(channelUrl, text);
    }, 500),
  ).current;

  // MutationObserver로 입력 필드 감시
  useEffect(() => {
    if (!currentChannel || !showChannel || isUpdatingChannel) return;

    const observeMessageInput = () => {
      const messageInput = document.querySelector(
        ".sendbird-message-input-text-field",
      ) as HTMLTextAreaElement;

      if (!messageInput) return;

      // 초기 초안 복원
      restoreDraftFromLocalStorage(currentChannel.url);

      // 입력 이벤트 리스너
      const handleInput = (e: Event) => {
        const target = e.target as HTMLTextAreaElement;
        const text = target.value || '';
        debouncedSaveDraft(currentChannel.url, text);
      };

      messageInput.addEventListener("input", handleInput);

      // Cleanup
      return () => {
        messageInput.removeEventListener("input", handleInput);
      };
    };

    // DOM 변경 감지
    if (!draftObserverRef.current) {
      draftObserverRef.current = new MutationObserver(() => {
        observeMessageInput();
      });
    }

    const targetNode = document.querySelector(".sendbird-conversation");
    if (targetNode) {
      draftObserverRef.current.observe(targetNode, {
        childList: true,
        subtree: true,
      });
      observeMessageInput();
    }

    return () => {
      if (draftObserverRef.current) {
        draftObserverRef.current.disconnect();
      }
    };
  }, [
    currentChannel,
    showChannel,
    isUpdatingChannel,
    debouncedSaveDraft,
    restoreDraftFromLocalStorage,
  ]);

  // 채널 변경 시 초안 처리
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showChannel || !currentChannel) return;

    const messageInput = document.querySelector(
      ".sendbird-message-input-text-field",
    ) as HTMLTextAreaElement;

    if (messageInput) {
      const currentText = messageInput.value;
      if (currentText && currentText.trim()) {
        // 이전 채널의 초안 저장
        saveDraftToLocalStorage(currentChannel.url, currentText);
      }
    }

    // 새 채널의 초안 복원
    restoreDraftFromLocalStorage(currentChannel.url);
  }, [
    currentChannel?.url,
    showChannel,
    saveDraftToLocalStorage,
    restoreDraftFromLocalStorage,
  ]);

  // 모달 닫을 때 초안 저장
  const saveDraftOnClose = useCallback(() => {
    if (!currentChannel) return;

    const messageInput = document.querySelector(
      ".sendbird-message-input-text-field",
    ) as HTMLTextAreaElement;

    if (messageInput) {
      const text = messageInput.value;
      if (text && text.trim()) {
        saveDraftToLocalStorage(currentChannel.url, text);
      }
    }
  }, [currentChannel, saveDraftToLocalStorage]);

  return {
    saveDraftToLocalStorage,
    deleteDraftFromLocalStorage,
    restoreDraftFromLocalStorage,
    saveDraftOnClose,
  };
};
